// src/core/hooks/useUrlListState.ts
import { useCallback, useMemo } from "react"
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid-pro"
import { useUrlFilters, UrlFilterSchema } from "./useUrlFilters"

/** Максимум страницы списковых GET бэкенда (дельта 2026-09-11: limit 1–500). */
export const MAX_LIST_LIMIT = 500

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100]

const LIST_STATE_SCHEMA = {
  page: "number",
  pageSize: "number",
  sortBy: "string",
  order: "string",
} as const satisfies UrlFilterSchema

export interface UrlListStateOptions {
  /** Размер страницы по умолчанию (дефолт 50 — дефолт бэка). */
  defaultPageSize?: number
  /** Опции пагинатора грида; все должны быть ≤ MAX_LIST_LIMIT. */
  pageSizeOptions?: readonly number[]
  /** Дефолтная колонка сортировки; не пишется в URL. */
  defaultSortBy?: string
  /** Дефолтное направление; не пишется в URL. */
  defaultOrder?: "ASC" | "DESC"
}

export interface UrlListState {
  page: number
  pageSize: number
  sortBy?: string
  order: "ASC" | "DESC"
  paginationModel: GridPaginationModel
  setPaginationModel: (model: GridPaginationModel) => void
  sortModel: GridSortModel
  onSortModelChange: (model: GridSortModel) => void
  /** Готовые аргументы RTK Query: { limit, offset, sortBy?, order? }. */
  listArgs: {
    limit: number
    offset: number
    sortBy?: string
    order?: "ASC" | "DESC"
  }
  /** Готовые пропсы DataGrid (rows/rowCount/loading передаёт таблица). */
  gridProps: {
    pagination: true
    paginationMode: "server"
    sortingMode: "server"
    paginationModel: GridPaginationModel
    onPaginationModelChange: (model: GridPaginationModel) => void
    sortModel: GridSortModel
    onSortModelChange: (model: GridSortModel) => void
    pageSizeOptions: number[]
  }
}

/**
 * Серверная пагинация + сортировка со синхронизацией в URL.
 * Единый useUrlFilters-схема (page/pageSize/sortBy/order) — один write
 * на изменение, ключи URL совпадают с useUrlPagination/useUrlSorting.
 */
export function useUrlListState(opts?: UrlListStateOptions): UrlListState {
  const { read, write } = useUrlFilters(LIST_STATE_SCHEMA)
  const {
    page: urlPage,
    pageSize: urlPageSize,
    sortBy: urlSortBy,
    order: urlOrder,
  } = read()

  const defaultPageSize = opts?.defaultPageSize ?? 50
  const defaultOrder: "ASC" | "DESC" = opts?.defaultOrder ?? "DESC"
  const pageSizeOptions = useMemo(
    () =>
      (opts?.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS).map((n) =>
        Math.min(n, MAX_LIST_LIMIT),
      ),
    [opts?.pageSizeOptions],
  )

  const page = urlPage && urlPage > 0 ? urlPage : 0
  const pageSize = Math.min(
    Math.max(urlPageSize ?? defaultPageSize, 1),
    MAX_LIST_LIMIT,
  )

  const sortBy = urlSortBy ?? opts?.defaultSortBy
  // нормализация регистра, как в useUrlSorting (order=asc руками в URL)
  const normalized: "ASC" | "DESC" | undefined =
    urlOrder != null && urlOrder.toUpperCase() === "ASC"
      ? "ASC"
      : urlOrder != null && urlOrder.toUpperCase() === "DESC"
        ? "DESC"
        : undefined
  const order: "ASC" | "DESC" = normalized ?? defaultOrder

  const setPaginationModel = useCallback(
    (model: GridPaginationModel) => {
      const current = read()
      write({
        page: model.page === 0 ? undefined : model.page,
        pageSize:
          model.pageSize === defaultPageSize ? undefined : model.pageSize,
        sortBy: current.sortBy,
        order: current.order,
      })
    },
    [write, read, defaultPageSize],
  )

  const sortModel: GridSortModel = useMemo(
    () =>
      sortBy ? [{ field: sortBy, sort: order === "ASC" ? "asc" : "desc" }] : [],
    [sortBy, order],
  )

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      const current = read()
      const first = model[0]
      const preservedPageSize =
        current.pageSize === defaultPageSize ? undefined : current.pageSize
      if (!first) {
        write({
          sortBy: undefined,
          order: undefined,
          page: undefined,
          pageSize: preservedPageSize,
        })
        return
      }
      const nextOrder: "ASC" | "DESC" = first.sort === "asc" ? "ASC" : "DESC"
      const isDefault =
        opts?.defaultSortBy != null &&
        first.field === opts.defaultSortBy &&
        nextOrder === defaultOrder
      write({
        sortBy: isDefault ? undefined : first.field,
        order: isDefault ? undefined : nextOrder,
        page: undefined, // смена сортировки — на первую страницу
        pageSize: preservedPageSize,
      })
    },
    [write, read, opts?.defaultSortBy, defaultOrder, defaultPageSize],
  )

  const listArgs = useMemo(() => {
    const args: UrlListState["listArgs"] = {
      limit: pageSize,
      offset: page * pageSize,
    }
    if (sortBy) args.sortBy = sortBy
    if (urlOrder != null || sortBy) args.order = order
    return args
  }, [page, pageSize, sortBy, order, urlOrder])

  const gridProps: UrlListState["gridProps"] = {
    pagination: true,
    paginationMode: "server",
    sortingMode: "server",
    paginationModel: { page, pageSize },
    onPaginationModelChange: setPaginationModel,
    sortModel,
    onSortModelChange,
    pageSizeOptions,
  }

  return {
    page,
    pageSize,
    sortBy,
    order,
    paginationModel: { page, pageSize },
    setPaginationModel,
    sortModel,
    onSortModelChange,
    listArgs,
    gridProps,
  }
}
